/**
 * Note and KeySignature migration utilities
 *
 * This module provides functions to convert between V1 (string) and V2 (object) formats
 * for notes and key signatures.
 */
// ============================================================================
// Note Migration
// ============================================================================
/**
 * Parse a V1 note string into a V2 Note object.
 *
 * Handles both ASCII and Unicode accidentals:
 * - "F#" or "F♯" → { noteName: 'F', modifier: 's' }
 * - "Bb" or "B♭" → { noteName: 'B', modifier: 'f' }
 * - "C" → { noteName: 'C', modifier: 'n' }
 *
 * @param v1Note - V1 format note string (e.g., "F♯", "Bb", "C")
 * @returns V2 format Note object
 * @throws Error if note string is invalid
 *
 * @example
 * parseNote("F♯")  // { noteName: 'F', modifier: 's' }
 * parseNote("Bb")  // { noteName: 'B', modifier: 'f' }
 * parseNote("C")   // { noteName: 'C', modifier: 'n' }
 */
export function parseNote(v1Note) {
    if (!v1Note || v1Note.length === 0) {
        throw new Error('Note string cannot be empty');
    }
    const noteName = v1Note[0].toUpperCase();
    if (!['C', 'D', 'E', 'F', 'G', 'A', 'B'].includes(noteName)) {
        throw new Error(`Invalid note name: ${noteName}`);
    }
    // Check for accidental (ASCII or Unicode)
    let modifier = 'n';
    if (v1Note.length > 1) {
        const accidental = v1Note.slice(1);
        if (accidental === '#' || accidental === '♯') {
            modifier = 's';
        }
        else if (accidental === 'b' || accidental === '♭') {
            modifier = 'f';
        }
        else {
            throw new Error(`Invalid accidental: ${accidental}`);
        }
    }
    return {
        noteName: noteName,
        modifier
    };
}
/**
 * Parse a V1 note string into a V2 Note object, with null handling.
 *
 * @param v1Note - V1 format note string or null
 * @returns V2 format Note object or null
 * @throws Error if note string is invalid (but not if null)
 *
 * @example
 * parseNoteNullable("F♯")  // { noteName: 'F', modifier: 's' }
 * parseNoteNullable(null)  // null
 * parseNoteNullable(undefined)  // null
 */
export function parseNoteNullable(v1Note) {
    if (v1Note == null)
        return null;
    return parseNote(v1Note);
}
/**
 * Parse a V1 key signature string into a V2 KeySignature object.
 *
 * Examples:
 * - "F♯m" → { note: { noteName: 'F', modifier: 's' }, mode: 'min' }
 * - "C" → { note: { noteName: 'C', modifier: 'n' }, mode: 'maj' }
 *
 * @param v1Key - V1 format key string (e.g., "F♯m", "C")
 * @returns V2 format KeySignature object
 * @throws Error if key string is invalid
 *
 * @example
 * parseKeySignature("F♯m")  // { note: { noteName: 'F', modifier: 's' }, mode: 'min' }
 * parseKeySignature("C")    // { note: { noteName: 'C', modifier: 'n' }, mode: 'maj' }
 */
export function parseKeySignature(v1Key) {
    if (!v1Key || v1Key.length === 0) {
        throw new Error('Key signature string cannot be empty');
    }
    // Check for minor mode (ends with 'm')
    const isMinor = v1Key.endsWith('m');
    const noteStr = isMinor ? v1Key.slice(0, -1) : v1Key;
    // Parse the note
    const note = parseNote(noteStr);
    return {
        note,
        mode: isMinor ? 'min' : 'maj'
    };
}
/**
 * Parse a V1 key signature string into a V2 KeySignature object, with null handling.
 *
 * @param v1Key - V1 format key string or null
 * @returns V2 format KeySignature object or null
 * @throws Error if key string is invalid (but not if null)
 *
 * @example
 * parseKeySignatureNullable("F♯m")  // { note: { noteName: 'F', modifier: 's' }, mode: 'min' }
 * parseKeySignatureNullable(null)   // null
 * parseKeySignatureNullable(undefined)  // null
 */
export function parseKeySignatureNullable(v1Key) {
    if (v1Key == null)
        return null;
    return parseKeySignature(v1Key);
}
